import React, { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Loader2, MapPin } from "lucide-react";

/**
 * Address autocomplete using Photon (komoot) — free, no API key needed.
 * As the user types, a dropdown with matching Belgian/EU street addresses
 * appears. Selecting one populates street, postcode and city in one shot,
 * eliminating typos that would otherwise break travel-cost geocoding.
 *
 * Props:
 *   value         - current street string (controlled)
 *   onChange      - (street: string) => void — fires on every keystroke
 *   onSelect      - ({street, postcode, city}) => void — fires when user picks a suggestion
 *   placeholder, className, "data-testid" - forwarded to <Input>
 */
export default function AddressAutocomplete({
    value,
    onChange,
    onSelect,
    placeholder = "Kerkstraat 12",
    className = "",
    "data-testid": testId = "address-autocomplete",
}) {
    const [suggestions, setSuggestions] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [highlight, setHighlight] = useState(-1);
    // Set after a user picks a suggestion → prevents the very next render
    // (triggered by parent's setForm) from re-querying for the same string.
    const justSelectedRef = useRef(false);
    const wrapperRef = useRef(null);
    const debounceRef = useRef(null);

    // Debounced fetch on value change
    useEffect(() => {
        if (justSelectedRef.current) {
            justSelectedRef.current = false;
            return;
        }
        const q = (value || "").trim();
        if (q.length < 3) {
            setSuggestions([]);
            setOpen(false);
            return;
        }
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            setLoading(true);
            try {
                const url = new URL("https://photon.komoot.io/api/");
                url.searchParams.set("q", q);
                url.searchParams.set("limit", "6");
                url.searchParams.set("lang", "default");
                // Bias results towards Belgium centre — without this, "Kerkstraat"
                // returns matches from all over Europe
                url.searchParams.set("lat", "50.85");
                url.searchParams.set("lon", "4.35");
                const resp = await fetch(url.toString(), {
                    headers: { Accept: "application/json" },
                });
                if (!resp.ok) {
                    setSuggestions([]);
                    setOpen(false);
                    return;
                }
                const data = await resp.json();
                const feats = (data.features || [])
                    .map((f) => {
                        const p = f.properties || {};
                        const c = f.geometry?.coordinates || [];
                        // Only keep results that look like real addresses (have a street name)
                        const street = p.name || p.street || "";
                        const city = p.city || p.town || p.village || p.county || "";
                        const postcode = p.postcode || "";
                        const country = p.country || "";
                        const housenumber = p.housenumber || "";
                        return {
                            label: [
                                street + (housenumber ? ` ${housenumber}` : ""),
                                postcode ? `${postcode} ${city}`.trim() : city,
                                country,
                            ].filter(Boolean).join(", "),
                            street: street + (housenumber ? ` ${housenumber}` : ""),
                            postcode,
                            city,
                            country,
                            lat: c[1],
                            lon: c[0],
                        };
                    })
                    // Drop results without a street name (cities/POIs)
                    .filter((s) => s.street && s.city)
                    // Prefer BE results visually but keep nearby NL/FR/LU as fallback
                    .sort((a, b) => {
                        const aBE = a.country?.toLowerCase().startsWith("belg") ? 0 : 1;
                        const bBE = b.country?.toLowerCase().startsWith("belg") ? 0 : 1;
                        return aBE - bBE;
                    });
                setSuggestions(feats);
                setOpen(feats.length > 0);
                setHighlight(-1);
            } catch {
                setSuggestions([]);
                setOpen(false);
            } finally {
                setLoading(false);
            }
        }, 280);
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [value]);

    // Close when clicking outside
    useEffect(() => {
        const handler = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const pick = (s) => {
        justSelectedRef.current = true;
        setOpen(false);
        setSuggestions([]);
        setHighlight(-1);
        onSelect?.({ street: s.street, postcode: s.postcode, city: s.city, country: s.country });
    };

    const onKeyDown = (e) => {
        if (!open || suggestions.length === 0) return;
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlight((h) => Math.min(suggestions.length - 1, h + 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlight((h) => Math.max(-1, h - 1));
        } else if (e.key === "Enter" && highlight >= 0) {
            e.preventDefault();
            pick(suggestions[highlight]);
        } else if (e.key === "Escape") {
            setOpen(false);
        }
    };

    return (
        <div ref={wrapperRef} className="relative">
            <Input
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={onKeyDown}
                onFocus={() => suggestions.length > 0 && setOpen(true)}
                placeholder={placeholder}
                className={className}
                data-testid={testId}
                autoComplete="off"
                spellCheck="false"
            />
            {loading && (
                <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500 animate-spin pointer-events-none" />
            )}
            {open && suggestions.length > 0 && (
                <div
                    className="absolute z-50 left-0 right-0 mt-1 bg-[#FFFFFF] border border-zinc-200 rounded-sm shadow-2xl max-h-72 overflow-auto"
                    data-testid={`${testId}-dropdown`}
                >
                    {suggestions.map((s, i) => (
                        <button
                            type="button"
                            key={`${s.lat}-${s.lon}-${i}`}
                            onMouseDown={(e) => { e.preventDefault(); pick(s); }}
                            onMouseEnter={() => setHighlight(i)}
                            className={`w-full text-left px-3 py-2 flex items-start gap-2 transition border-b border-zinc-100 last:border-0 ${highlight === i ? "bg-[#E5A93D]/10" : "hover:bg-zinc-100"}`}
                            data-testid={`${testId}-option-${i}`}
                        >
                            <MapPin className="h-3.5 w-3.5 text-[#E5A93D] mt-1 flex-shrink-0" />
                            <div className="min-w-0">
                                <div className="text-sm text-zinc-950 truncate">{s.street}</div>
                                <div className="text-xs text-zinc-500 truncate">
                                    {s.postcode ? `${s.postcode} ` : ""}{s.city}{s.country ? ` · ${s.country}` : ""}
                                </div>
                            </div>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
